import { evaluateSlideLimits, matchesRule } from './slideLimitEvaluator'
import { resolveSlideLimits } from './slideLimitRuleset'

export const FREE_PLAN_SLIDE_LIMITS = Object.freeze({
  quiz: 5,
  poll: 3,
})

export const FREE_PLAN_QUIZ_SLIDE_TYPES = Object.freeze([
  'pickAnswer',
])

export const FREE_PLAN_POLL_SLIDE_TYPES = Object.freeze([
  'multipleChoice',
  'imageChoice',
  'wordCloud',
  'openEnded',
  'scale',
  'ideas',
  'ranking',
  'pinOnImage',
  'ideaBoard',
])

// Display names for each type — kept next to the type lists so they stay in sync
export const FREE_PLAN_QUIZ_TYPE_NAMES = Object.freeze({
  pickAnswer: 'Pick Answer',
  typeAnswer: 'Short Answer',
  matchPairs: 'Match Pair',
  correctOrder: 'Correct Order',
  categorise: 'Categorise',
})

export const FREE_PLAN_POLL_TYPE_NAMES = Object.freeze({
  poll: 'Poll',
  wordCloud: 'Word Cloud',
  openEnded: 'Open Ended',
  brainstorm: 'Brainstorm',
  ideaBoard: 'Idea Board',
  ranking: 'Ranking',
  pinOnImage: 'Pin on Image',
})

export const isFreePlanQuizType = (item) => {
  return FREE_PLAN_QUIZ_SLIDE_TYPES.includes(item && item.type)
}

export const isFreePlanPollType = (item) => {
  return FREE_PLAN_POLL_SLIDE_TYPES.includes(item && item.type)
}

const _groupSlidesToRows = (slides) => {
  const rowMap = new Map()
  const safeSlides = Array.isArray(slides) ? slides : []
  safeSlides.forEach((slide) => {
    if (!slide || !slide.type) return
    const { type } = slide
    const slideType = slide.slideType || null
    const key = `${type}\0${slideType}`
    if (!rowMap.has(key)) rowMap.set(key, { type, slideType, count: 0 })
    rowMap.get(key).count += 1
  })
  return Array.from(rowMap.values())
}

const _enrich = (rule) => ({
  ...rule,
  remaining: rule.limit === false ? Infinity : Math.max(rule.limit - rule.count, 0),
})

const _isQuizMatchType = (match) => Array.isArray(match && match.type) && match.type.includes('pickAnswer')

const _aliasFields = (rule, prefix) => ({
  [`${prefix}Used`]: rule ? rule.count : 0,
  [`${prefix}Limit`]: rule ? rule.limit : false,
  [`${prefix}Remaining`]: rule ? rule.remaining : Infinity,
})

/**
 * @param {Array} slides
 * @param {Object} planBenefitLimitation
 * @param {Object|undefined} slideLimits - Served ruleset from state.planBenefit.slideLimits; falls back to CONTROL when absent/invalid.
 * @returns {{ quizUsed, quizLimit, quizRemaining, pollUsed, pollLimit, pollRemaining, isQuizLocked, isPollLocked, isOverCapacity, perRule, unmatched }}
 */
export const getFreeSlideUsage = (slides = [], planBenefitLimitation = {}, slideLimits = undefined) => {
  const resolved = resolveSlideLimits(slideLimits)
  const {
    perRule, unmatched, distinctTypes, isOverCapacity,
  } = evaluateSlideLimits(_groupSlidesToRows(slides), resolved)

  const enrichedPerRule = perRule.map((rule, i) => _enrich({ ...rule, label: resolved.rules[i] && resolved.rules[i].label, match: resolved.rules[i] && resolved.rules[i].match }))
  const enrichedUnmatched = _enrich(unmatched)

  const quizRuleIndex = resolved.rules.findIndex((rule) => _isQuizMatchType(rule.match))
  let pollRuleIndex
  if (quizRuleIndex === -1) {
    pollRuleIndex = resolved.rules.length > 0 ? 0 : -1
  } else {
    pollRuleIndex = resolved.rules.findIndex((_, i) => i !== quizRuleIndex)
  }

  const quizRule = quizRuleIndex !== -1 ? enrichedPerRule[quizRuleIndex] : null
  const pollRule = pollRuleIndex !== -1 ? enrichedPerRule[pollRuleIndex] : null

  return {
    ..._aliasFields(quizRule, 'quiz'),
    ..._aliasFields(pollRule, 'poll'),
    isQuizLocked: Boolean(planBenefitLimitation && planBenefitLimitation.quiz),
    isPollLocked: Boolean(planBenefitLimitation && planBenefitLimitation.poll),
    isOverCapacity,
    distinctTypes,
    perRule: enrichedPerRule,
    unmatched: enrichedUnmatched,
  }
}

const _isCountedDistinctIdentity = (item, distinctTypes) => {
  if (!distinctTypes || !distinctTypes.isOver) return false
  const itemSlideType = item.slideType || null
  return distinctTypes.identities.some(
    (identity) => identity.type === item.type && identity.slideType === itemSlideType,
  )
}

export const isFreePlanUsageLockedForSlideType = (item, freeSlideUsage = {}) => {
  if (!item || !item.type) return false
  if (_isCountedDistinctIdentity(item, freeSlideUsage.distinctTypes)) return true
  const rules = Array.isArray(freeSlideUsage.perRule) ? freeSlideUsage.perRule : []
  const matchedRule = rules.find((entry) => matchesRule(item, entry.match))
  if (matchedRule) return Boolean(matchedRule.isOver)
  const unmatched = freeSlideUsage.unmatched || {}
  const unmatchedRows = Array.isArray(unmatched.rows) ? unmatched.rows : []
  const itemSlideType = item.slideType || null
  const row = unmatchedRows.find((entry) => entry.type === item.type && entry.slideType === itemSlideType)
  return Boolean(row && row.isOver)
}

export const getFreeSlideUsageLegacy = (slides = [], planBenefitLimitation = {}) => {
  const safeSlides = Array.isArray(slides) ? slides : []

  const quizUsed = safeSlides.filter(isFreePlanQuizType).length
  const pollUsed = safeSlides.filter(isFreePlanPollType).length

  return {
    quizUsed,
    quizLimit: FREE_PLAN_SLIDE_LIMITS.quiz,
    quizRemaining: Math.max(FREE_PLAN_SLIDE_LIMITS.quiz - quizUsed, 0),
    pollUsed,
    pollLimit: FREE_PLAN_SLIDE_LIMITS.poll,
    pollRemaining: Math.max(FREE_PLAN_SLIDE_LIMITS.poll - pollUsed, 0),
    isQuizLocked: Boolean(planBenefitLimitation && planBenefitLimitation.quiz),
    isPollLocked: Boolean(planBenefitLimitation && planBenefitLimitation.poll),
    isOverCapacity: quizUsed > FREE_PLAN_SLIDE_LIMITS.quiz
      || pollUsed > FREE_PLAN_SLIDE_LIMITS.poll,
  }
}

export const isFreePlanUsageLockedForSlideTypeLegacy = (item, freeSlideUsage = {}) => {
  if (isFreePlanQuizType(item)) {
    return Boolean(freeSlideUsage.isQuizLocked)
  }
  if (isFreePlanPollType(item)) {
    return Boolean(freeSlideUsage.isPollLocked)
  }
  return false
}
